// eslint-disable-next-line filenames/match-regex
import {PlusIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {AddedToProjectV2Event$key} from './__generated__/AddedToProjectV2Event.graphql'
import {TimelineRow} from './row/TimelineRow'
import {ProjectV2} from './ProjectV2'
import {Fragment} from 'react'
import {RolledupProjectV2Event} from './RolledupProjectV2Event'

type AddedToProjectV2EventProps = {
  queryRef: AddedToProjectV2Event$key
  issueUrl: string
  rollupGroup?: Record<string, Array<AddedToProjectV2Event$key | AddedToProjectV2Event$key>>
}

const AddedToProjectV2EventFragment = graphql`
  fragment AddedToProjectV2Event on AddedToProjectV2Event {
    createdAt
    actor {
      ...TimelineRowEventActor
    }
    project {
      title
      url
    }
  }
`

export function AddedToProjectV2Event({queryRef, issueUrl, rollupGroup}: AddedToProjectV2EventProps): JSX.Element {
  const {actor, createdAt, project} = useFragment(AddedToProjectV2EventFragment, queryRef)

  if (!project) {
    return <></>
  }

  // Once we have support for the project IDs being exposed in GQL we can shift to using the
  // `issueEventExternalUrl` function here for our deep linked timestamp.
  return (
    <TimelineRow highlighted={false} actor={actor} createdAt={createdAt} deepLinkUrl={issueUrl} leadingIcon={PlusIcon}>
      <TimelineRow.Main>
        {rollupGroup ? (
          <RolledupProjectV2Event rollupGroup={rollupGroup} />
        ) : (
          <AddedToProjectV2sRendering queryRefs={[queryRef]} />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}

export const AddedToProjectV2sRendering = ({queryRefs}: {queryRefs: AddedToProjectV2Event$key[]}) => {
  if (queryRefs.length === 0) {
    return null
  }

  return (
    <>
      {`${LABELS.timeline.addedThisTo} `}
      {queryRefs.map((queryRef, index) => (
        <Fragment key={index}>
          <InternalAddedToProjectV2sRendering
            queryRef={queryRef}
            first={index === 0}
            last={index === queryRefs.length - 1}
          />
        </Fragment>
      ))}
    </>
  )
}

const InternalAddedToProjectV2sRendering = ({
  queryRef,
  first,
  last,
}: Pick<AddedToProjectV2EventProps, 'queryRef'> & {
  first: boolean
  last: boolean
}) => {
  const {project} = useFragment(AddedToProjectV2EventFragment, queryRef)

  if (!project?.title || !project?.url) {
    return null
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        marginRight: 0,
        '&:last-of-type': {
          marginRight: 1,
        },
      }}
    >
      <>
        {!first && !last && <Box sx={{mr: 1}}>,</Box>}
        {!first && last && <Box sx={{mr: 1, ml: 1}}>{LABELS.timeline.and}</Box>}
        <ProjectV2 title={project?.title} url={project?.url} />
      </>
    </Box>
  )
}

try{ AddedToProjectV2Event.displayName ||= 'AddedToProjectV2Event' } catch {}
try{ AddedToProjectV2sRendering.displayName ||= 'AddedToProjectV2sRendering' } catch {}
try{ InternalAddedToProjectV2sRendering.displayName ||= 'InternalAddedToProjectV2sRendering' } catch {}